import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { ContactBand, PhotoEnquiryBand } from "@/components/gisela/ClosingBands"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import WorkingTogether from "@/components/gisela/WorkingTogether"
import { sectionHidden } from "@/lib/section-visibility"
import type { BaseBlockData } from "@/components/gisela/SanctuaryBaseBlock"
import type { PageMeta, PageProps, PlacementPhoto } from "@/types/inertia"

interface Props {
  content: {
    hero: { heading?: string; subheading?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    /** The home page's band, read from the same section. */
    working_together: React.ComponentProps<typeof WorkingTogether>["content"]
    cta?: { photo?: HeroPhoto | null; _hidden?: boolean }
    contact?: { heading?: string; body?: string; _hidden?: boolean }
  }
  base?: BaseBlockData | null
  online_photo?: PlacementPhoto | null
  tile_photos?: Record<string, PlacementPhoto | null>
  contact_photo?: PlacementPhoto | null
  meta?: PageMeta
}

/**
 * The landing page behind the "Where we meet" menu: a hero, then the home
 * page's own band — online, Prestwich, Hebden Bridge — so a visitor who
 * clicks the menu label rather than an item gets the overview, the way the
 * conditions and therapies menus land on their index pages.
 */
export default function WhereWeMeet({ content, base, online_photo, tile_photos = {}, contact_photo, meta }: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.meet
  const title = meta?.title ?? t.meta_title
  const description = meta?.description ?? t.meta_description

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo}>
            <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">{t.eyebrow}</p>
            <h1 className="text-white">{content.hero.heading || t.hero_heading}</h1>
            {content.hero.subheading && (
              <p className="mt-4 max-w-2xl text-lg text-white/80">{content.hero.subheading}</p>
            )}
          </HeroBand>
        )}

        <WorkingTogether content={content.working_together} base={base} photo={online_photo} heading={t.band_heading} />

        {!sectionHidden(content.cta) && (
          <ContactBand heading={t.cta_heading} body={t.cta_body} photo={content.cta?.photo} />
        )}

        <GiselaPageTiles photos={tile_photos} />

        {!sectionHidden(content.contact) && (
          <PhotoEnquiryBand heading={content.contact?.heading} body={content.contact?.body} photo={contact_photo} />
        )}
      </PublicLayout>
    </>
  )
}
